/**
 * The change feed.
 *
 * Only comparable numeric price events are listed as changes. First
 * observations, corrections and source switches are real events too, but they
 * are a different question and live behind the "kind" control rather than being
 * mixed into a price movement.
 */

import { useQuery } from "@tanstack/react-query";

import type { PriceChangeRow, PricingStatistics } from "@pricing/contracts";
import { client } from "../lib/api.ts";
import { contextParams } from "../lib/url-state.ts";
import { ContextBar } from "../components/ContextBar.tsx";
import { StatsStrip } from "../components/StatsStrip.tsx";
import { SellerSelect } from "../components/SellerSelect.tsx";
import { TextFilter, validateInstant } from "../components/TextFilter.tsx";
import { formatInstant, formatPercent, formatUsd } from "../lib/format.ts";
import { describeShown, usePagedQuery } from "../lib/paging.ts";
import type { ViewProps } from "./PricesView.tsx";

const PERIODS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
] as const;

export function ChangesView({ state, update }: ViewProps): React.JSX.Element {
  // Statistics take the same period and filters but no paging: the aggregate
  // is one object, and sending `limit` would be an undeclared parameter.
  // The API takes both bounds of a period or neither, and refuses one alone.
  // A half-typed period is held back, and the page says so, instead of being
  // sent to be rejected.
  const halfPeriod = (state.from_utc === "") !== (state.to_utc === "");
  const filters = {
    ...contextParams(state),
    ...(state.from_utc && state.to_utc ? { from_utc: state.from_utc, to_utc: state.to_utc } : {}),
    ...(state.direction ? { direction: state.direction } : {}),
    ...(state.bucket ? { bucket: state.bucket } : {}),
    ...(state.billing_provider_id ? { billing_provider_id: state.billing_provider_id } : {}),
  };
  const params = { ...filters, limit: 200 };

  const {
    query: changes,
    rows,
    first,
  } = usePagedQuery<PriceChangeRow, Awaited<ReturnType<typeof client.priceChanges>>>(
    ["changes", params],
    (cursor) => client.priceChanges((cursor ? { cursor } : params) as never),
  );
  const statistics = useQuery({
    queryKey: ["change-statistics", filters],
    queryFn: () => client.statistics(filters as never),
  });

  const setPeriod = (days: number): void => {
    const cutoff = first?.observation_cutoff_utc ?? new Date().toISOString();
    const to = new Date(Date.parse(cutoff));
    const from = new Date(to.getTime() - days * 86_400_000);
    update({ from_utc: from.toISOString(), to_utc: to.toISOString() });
  };

  return (
    <>
      <section className="panel">
        <h2 className="panel__title">Period and filters</h2>
        <div className="filters">
          {PERIODS.map((period) => (
            <button
              key={period.days}
              type="button"
              className="button"
              onClick={() => setPeriod(period.days)}
            >
              Last {period.label}
            </button>
          ))}
          <TextFilter
            label="From (UTC)"
            value={state.from_utc}
            validate={validateInstant}
            onCommit={(value) => update({ from_utc: value }, { replace: true })}
          />
          <TextFilter
            label="To (UTC)"
            value={state.to_utc}
            validate={validateInstant}
            onCommit={(value) => update({ to_utc: value }, { replace: true })}
          />
          <SellerSelect
            value={state.billing_provider_id}
            onChange={(value) => update({ billing_provider_id: value })}
          />
          <label className="field">
            Direction
            <select
              value={state.direction}
              onChange={(event) => update({ direction: event.target.value })}
            >
              <option value="">Both</option>
              <option value="increase">Increases</option>
              <option value="decrease">Decreases</option>
            </select>
          </label>
          <label className="field">
            Bucket
            <select value={state.bucket} onChange={(event) => update({ bucket: event.target.value })}>
              <option value="">All buckets</option>
              <option value="input">Input</option>
              <option value="output">Output</option>
              <option value="cache_read">Cache read</option>
              <option value="cache_write">Cache write</option>
              <option value="cache_write_5m">Cache write 5m</option>
              <option value="cache_write_1h">Cache write 1h</option>
            </select>
          </label>
        </div>
        {halfPeriod ? (
          <p className="notice notice--warn" style={{ marginTop: "var(--space-3)" }}>
            Set both From and To to choose a period. Until then, the last 30 days are shown.
          </p>
        ) : null}
      </section>

      <StatsStrip statistics={(statistics.data?.data ?? null) as PricingStatistics | null} />

      <section className="panel">
        <h2 className="panel__title">
          Price changes{" "}
          {rows.length > 0 ? (
            <span className="badge">{describeShown(rows.length, changes.hasNextPage, "change")}</span>
          ) : null}
        </h2>
        {changes.isError ? (
          // Under the filters, not in place of them: the value that caused it
          // can be corrected where it was typed.
          <p className="notice notice--danger" role="alert">
            Could not load changes: {(changes.error as Error).message}
          </p>
        ) : null}
        {changes.isPending ? <p className="notice">Loading changes…</p> : null}
        {!changes.isPending && !changes.isError && rows.length === 0 ? (
          <p className="notice notice--empty">
            No comparable price change was detected in this period. First observations, corrections
            and source switches are reported separately and are not counted here.
          </p>
        ) : null}
        {rows.length > 0 ? (
          <div className="table-wrap">
            <table>
              <caption className="visually-hidden">
                Detected price changes, one row per source and bucket
              </caption>
              <thead>
                <tr>
                  <th scope="col">Detected</th>
                  <th scope="col">Offering</th>
                  <th scope="col">Bucket</th>
                  <th scope="col" className="numeric">
                    Old
                  </th>
                  <th scope="col" className="numeric">
                    New
                  </th>
                  <th scope="col" className="numeric">
                    Change
                  </th>
                  <th scope="col">Source</th>
                  <th scope="col">Occurred within</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((change) => (
                  <tr key={change.change_id}>
                    <th scope="row" title={formatInstant(change.detected_at_utc)}>
                      {toMinute(change.detected_at_utc)}
                    </th>
                    <td>
                      <OfferingLink
                        change={change}
                        onOpen={() => update({ view: "model", offering_id: change.offering_id })}
                      />
                    </td>
                    <td>{change.bucket.replace(/_/g, " ")}</td>
                    <td className="numeric">
                      {change.old_amount === null ? "—" : formatUsd(change.old_amount)}
                    </td>
                    <td className="numeric">
                      {change.new_amount === null ? "—" : formatUsd(change.new_amount)}
                    </td>
                    <td className="numeric">
                      {change.direction === "increase" ? "▲" : change.direction === "decrease" ? "▼" : "·"}{" "}
                      {formatPercent(change.percent_delta, change.label)}
                    </td>
                    <td>{change.source_id}</td>
                    <td title="Detection is not the vendor's effective date">
                      {change.uncertainty_from_utc
                        ? formatWindow(change.uncertainty_from_utc, change.detected_at_utc)
                        : "first observation"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {changes.hasNextPage ? (
          <button
            type="button"
            className="button load-more"
            disabled={changes.isFetchingNextPage}
            onClick={() => void changes.fetchNextPage()}
          >
            {changes.isFetchingNextPage ? "Loading…" : "Show more changes"}
          </button>
        ) : null}
      </section>

      {first ? (
        <ContextBar
          catalogRevisionId={first.catalog_revision_id}
          observationSequence={first.observation_sequence}
          observationCutoffUtc={first.observation_cutoff_utc}
        />
      ) : null}
    </>
  );
}

/**
 * An offering named as a person reads it, from the name the server put on
 * the change. For an offering this catalog version does not list, the ID
 * stands in.
 */
function OfferingLink({
  change,
  onOpen,
}: {
  readonly change: PriceChangeRow;
  readonly onOpen: () => void;
}): React.JSX.Element {
  return (
    <button type="button" className="link-button" onClick={onOpen} title={change.offering_id}>
      {change.model_display_name ?? `${change.offering_id.slice(0, 18)}…`}
      {change.offering ? (
        <span className="stat__denominator">
          {" "}
          {change.offering.billing_provider_id} · {change.offering.service_tier}
        </span>
      ) : null}
    </button>
  );
}

/** A UTC instant to the minute: "2026-09-18 06:00Z". */
function toMinute(value: string): string {
  return `${value.slice(0, 10)} ${value.slice(11, 16)}Z`;
}

/**
 * The interval a change happened in, without repeating a shared date:
 * "2026-09-18 00:00 → 06:00Z". Full timestamps side by side ran off the table.
 */
function formatWindow(from: string, to: string): string {
  const end = from.slice(0, 10) === to.slice(0, 10) ? `${to.slice(11, 16)}Z` : toMinute(to);
  return `${from.slice(0, 10)} ${from.slice(11, 16)} → ${end}`;
}
